import React from 'react';
import { ICampaign, IDevice, IUser } from '../../interfaces';
import { RevenueChart } from './RevenueCharts';

const MONTHS = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Aout',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre'
];

const computeRevenue = (campaigns: ICampaign[], startDate: Date) => {
    const start = new Date(startDate);

    const end = new Date(start);
    end.setHours(23, 59, 59, 999);

    let cursor = 0;
    const campaign_store: {
        [key: number]: ICampaign[];
    } = {};

    const days = [];

    while (cursor < campaigns.length && campaigns[cursor].endDate.getTime() < start.getTime()) cursor++;

    while (start.getFullYear() === startDate.getFullYear()) {
        const day: {
            date: Date;
            campaigns: ICampaign[];
        } = {
            date: new Date(start),
            campaigns: []
        };

        // Add all campaigns that begin that day
        while (cursor < campaigns.length && campaigns[cursor].startDate.getTime() <= start.getTime()) {
            const campaign_end = campaigns[cursor].endDate.getTime();

            if (campaign_end < start.getTime()) {
                cursor++;
                continue;
            }

            if (!(campaign_end in campaign_store)) campaign_store[campaign_end] = [];

            campaign_store[campaign_end].push(campaigns[cursor]);

            cursor++;
        }

        for (const campaigns of Object.values(campaign_store)) {
            for (const campaign of campaigns) {
                day.campaigns.push(campaign);
            }
        }

        // Delete campaigns that expire that day
        delete campaign_store[end.getTime()];

        start.setDate(start.getDate() + 1);
        end.setDate(end.getDate() + 1);

        days.push(day);
    }

    return days;
};

const getCampaignsRevenue = (campaigns: ICampaign[]) => {
    let revenue = 0;

    for (const c of campaigns) {
        const time = (c.endDate.getTime() + 1 - c.startDate.getTime()) / (1000 * 3600 * 24);
        const camp_revenue = Math.round((c.revenue || 0) / time);

        revenue += camp_revenue;
    }

    return revenue;
};

const getRevenueCumulative = (revenues: { date: Date; campaigns: ICampaign[] }[]) => {
    const sum = [];
    let count = 0;

    for (const { campaigns } of revenues) {
        const revenue = getCampaignsRevenue(campaigns);
        count += revenue;

        count = Math.round(count);

        sum.push(count);
    }

    return sum;
};

const getMonthlyRevenue = (revenues: { date: Date; campaigns: ICampaign[] }[]) => {
    const months = new Array(12).fill(null).map(() => ({ revenue: 0, revenue_sum: 0 }));

    let current_month = 0;
    let current_revenue = 0;
    let current_sum = 0;

    for (const r of revenues) {
        if (r.date.getMonth() !== current_month) {
            current_sum += current_revenue;

            months[current_month].revenue = current_revenue;
            months[current_month].revenue_sum = current_sum;

            current_revenue = 0;
            current_month = r.date.getMonth();
        }

        current_revenue += getCampaignsRevenue(r.campaigns);
    }

    months[current_month].revenue = current_revenue;
    months[current_month].revenue_sum = current_sum;

    return months;
};

const getWeeklyRevenue = (revenues: { date: Date; campaigns: ICampaign[] }[]) => {
    const weeks = new Array(53).fill(null).map(() => ({ revenue: 0, revenue_sum: 0 }));

    let current_week = 0;
    let current_revenue = 0;
    let current_sum = 0;

    for (const r of revenues) {
        if (r.date.getDay() === 1) {
            // Not a monday
            current_sum += current_revenue;

            weeks[current_week].revenue = current_revenue;
            weeks[current_week].revenue_sum = current_sum;

            current_revenue = 0;
            current_week++;
        }

        current_revenue += getCampaignsRevenue(r.campaigns);
    }

    weeks[current_week].revenue = current_revenue;
    weeks[current_week].revenue_sum = current_sum;

    return weeks;
};

const Revenue = (props: {
    campaigns: ICampaign[];
    devices: IDevice[];
    date: Date;
    users: IUser[];
    user: IUser;
    objective: number;
}) => {
    const yearStart = new Date(props.date);
    yearStart.setMonth(0);
    yearStart.setDate(1);
    yearStart.setHours(0, 0, 0, 0);

    const sortedCampaigns = props.campaigns.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    const days_revenue = computeRevenue(sortedCampaigns, yearStart);

    const revenues_daily = days_revenue.map(({ campaigns }) => getCampaignsRevenue(campaigns));
    const revenues_cumulative = getRevenueCumulative(days_revenue);
    const revenues_monthly = getMonthlyRevenue(days_revenue);
    const revenues_weekly = getWeeklyRevenue(days_revenue);

    return (
        <div className="revenue">
            <div className="panel">
                <RevenueChart
                    title="Revenus"
                    objective={props.objective}
                    revenue_daily={days_revenue.map((c, i) => ({
                        x: c.date.getTime() - c.date.getTimezoneOffset() * 60 * 1000,
                        y: revenues_daily[i]
                    }))}
                    revenue_cumulative={days_revenue.map((c, i) => ({
                        x: c.date.getTime() - c.date.getTimezoneOffset() * 60 * 1000,
                        y: revenues_cumulative[i]
                    }))}
                ></RevenueChart>
            </div>
            <div className="row">
                <div className="col-6">
                    <div className="panel">
                        <table>
                            <thead>
                                <tr>
                                    <th>Semaine #</th>
                                    <th>Revenu</th>
                                    <th>Revenu cumulé</th>
                                </tr>
                            </thead>
                            <tbody>
                                {revenues_weekly.map((w, i) => (
                                    <tr key={i}>
                                        <td>{i}</td>
                                        <td>{Intl.NumberFormat('fr-FR').format(w.revenue)}€</td>
                                        <td>{Intl.NumberFormat('fr-FR').format(w.revenue_sum)}€</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="col-6">
                    <div className="panel">
                        <table>
                            <thead>
                                <tr>
                                    <th>Mois</th>
                                    <th>Revenu</th>
                                    <th>Revenu cumulé</th>
                                </tr>
                            </thead>
                            <tbody>
                                {MONTHS.map((m, i) => (
                                    <tr key={m}>
                                        <td>{m}</td>
                                        <td>{Intl.NumberFormat('fr-FR').format(revenues_monthly[i].revenue)}€</td>
                                        <td>{Intl.NumberFormat('fr-FR').format(revenues_monthly[i].revenue_sum)}€</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Revenue;
